import { useEffect } from 'react';
import { createPortal } from 'react-dom';

// Centered modal shell shared by every booking modal.
//   * full-viewport dimmed backdrop (fixed inset-0)
//   * card centered vertically + horizontally, with padding so it never touches
//     the screen edges on mobile
//   * card capped at ~420px wide and at the viewport height — taller content
//     scrolls inside the card so its actions stay reachable
//   * background scroll locked while open, restored on close/unmount
//   * Escape closes (when onClose is provided; pass undefined to disable closing,
//     e.g. while an action is mid-flight)
// Children supply the card's inner content; the card chrome lives here so every
// modal inherits identical placement and behaviour.
export default function ModalShell({ onClose, closeOnBackdrop = true, ariaLabel, children }) {
  // Lock background scroll while mounted.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Escape to close.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portal to <body> so the fixed overlay is centered in the real viewport,
  // independent of scroll position or any transformed/filtered ancestor (a CSS
  // transform/filter/backdrop-filter/perspective on an ancestor would otherwise
  // make `position: fixed` resolve relative to that ancestor, not the viewport).
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[rgba(5,12,24,0.78)] backdrop-blur-sm"
        onClick={closeOnBackdrop && onClose ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="relative w-full max-w-[420px] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-[rgba(212,168,67,0.35)] bg-[#0D1E35] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-6 py-7 card-enter"
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
